import * as React from "react";
import {
  useFormContext,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Label } from "./label";
import { Input } from "./input";
import { cn } from "../../lib/utils";

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  label?: string;
  description?: string;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  render?: (
    field: {
      value: unknown;
      onChange: (...event: unknown[]) => void;
      onBlur: () => void;
      name: string;
      ref: React.Ref<unknown>;
      disabled?: boolean;
    },
    /**
     * Everything the default `Input` branch wires up, so a custom control does
     * not silently lose it. Spread onto the control:
     *
     * ```tsx
     * render={(field, a11y) => <Input {...a11y} ref={field.ref} … />}
     * ```
     *
     * Without `id` the `<Label htmlFor>` this component renders points at
     * nothing; without `aria-invalid` / `aria-describedby` the error copy below
     * is invisible to a screen reader. `className` carries only the
     * invalid-state classes — merge with `cn()` if the control needs its own.
     */
    a11y: {
      id: string;
      "aria-invalid": true | undefined;
      "aria-describedby": string | undefined;
      className: string | undefined;
    },
  ) => React.ReactNode;
}

function FormField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  placeholder,
  type = "text",
  className,
  required,
  disabled,
  render,
}: FormFieldProps<TFieldValues>) {
  const {
    control,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  /*
   * `name` is a path, so resolve it as one. A flat `errors[name]` lookup finds
   * nothing for a nested field like `github.apiBaseUrl` — the error lives at
   * `errors.github.apiBaseUrl` — which meant every nested field in the app
   * failed validation silently: the form refused to submit and showed no
   * reason. Unnested names behave exactly as before.
   */
  const error = resolveFieldError(errors, name);

  // Invalid field per `docs/borderless-contract.md` §1: `bg-danger-subtle` +
  // ``, never a red edge. The hover class has to be
  // respecified because `Input`'s own `hover:bg-field-strong` would otherwise
  // wash out the tint.
  const invalidClassName = error
    ? "bg-danger-subtle hover:bg-danger-subtle"
    : undefined;
  const describedBy = error
    ? `${name}-error`
    : description
      ? `${name}-description`
      : undefined;
  const a11y = {
    id: name,
    "aria-invalid": error ? (true as const) : undefined,
    "aria-describedby": describedBy,
    className: invalidClassName,
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className={cn("space-y-1.5", className)}>
          {label && (
            <Label htmlFor={name}>
              {label}
              {required && <span className="ml-0.5 text-danger">*</span>}
            </Label>
          )}
          {render ? (
            render(field, a11y)
          ) : (
            <Input
              {...field}
              {...a11y}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              value={field.value ?? ""}
              className={cn(a11y.className)}
            />
          )}
          {description && !error && (
            <p
              id={`${name}-description`}
              className="text-xs text-text-secondary"
            >
              {description}
            </p>
          )}
          {error && (
            <p id={`${name}-error`} className="text-xs text-danger">
              {error.message as string}
            </p>
          )}
        </div>
      )}
    />
  );
}

export { FormField };
export type { FormFieldProps };

/** Walk a dot path (`github.apiBaseUrl`) through react-hook-form's error tree. */
function resolveFieldError(
  errors: Record<string, unknown>,
  name: string,
): { message?: unknown } | undefined {
  let node: unknown = errors;
  for (const key of name.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[key];
  }
  return node && typeof node === "object" && "message" in node
    ? (node as { message?: unknown })
    : undefined;
}
